import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Alert, SubmitButton } from "./index.jsx";
// Imported directly rather than via ./index.jsx: index re-exports this
// component, so going through the barrel would add another edge to that cycle.
import OtpCaptcha from "./OtpCaptcha";
import useOtpCaptcha from "../hooks/useOtpCaptcha";

/**
 * Must match security.otp.resend_cooldown_seconds on the backend. The countdown
 * was 30s while the server refuses a resend for 60s, so the link invited a click
 * that could only fail. Where the server tells us how long to wait (a 429 with
 * `retry_after`) we use ITS number instead of this one, so the two cannot drift
 * again.
 */
const RESEND_COOLDOWN_SECONDS = 60;

/**
 * A wait can come from the hourly or daily budget, not just the resend
 * cooldown, so it is not always a handful of seconds — "Resend code in 3600s"
 * reads like a bug.
 */
const formatWait = (seconds) => {
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.ceil(seconds / 60);

  if (minutes < 60) return `${minutes} min`;

  const hours = Math.ceil(minutes / 60);

  return `${hours} hour${hours === 1 ? "" : "s"}`;
};

/** How long the "check your email" notice stays up. */
const EMAIL_NOTICE_MS = 6000;

// `sentByEmail`: the backend emailed the code (RC master admins) instead of
// texting it, so "we sent a code to <phone>" would point at the wrong place.
const OTPVerification = ({ phoneNumber, onVerified, onBack, sentByEmail = false }) => {
  const [byEmail, setByEmail] = useState(sentByEmail);
  const [showEmailNotice, setShowEmailNotice] = useState(sentByEmail);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRefs = useRef([]);
  const { recaptchaRef, enabled: captchaEnabled, siteKey, withToken } = useOtpCaptcha();

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (!showEmailNotice) return;
    const timer = setTimeout(() => setShowEmailNotice(false), EMAIL_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [showEmailNotice]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      setError("Please enter the full 6-digit code.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onVerified(otpCode);
    } catch (err) {
      setError(err.message || "Invalid OTP code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    try {
      const res = await axios.post("/api/send-otp", await withToken({ phoneNumber: phoneNumber }));
      const emailed = res?.data?.channel === "email";
      setByEmail(emailed);
      setShowEmailNotice(emailed);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      setResendTimer(RESEND_COOLDOWN_SECONDS);
      setError(null);
    } catch (err) {
      // A refused resend now carries the server's own wait time. Honour it, so
      // the countdown reflects the budget that actually applies (which may be
      // the hourly or daily ceiling, not just the resend cooldown) rather than
      // re-offering the link after our local guess.
      const retryAfter = Number(err.response?.data?.retry_after);

      if (Number.isFinite(retryAfter) && retryAfter > 0) {
        setResendTimer(retryAfter);
      }

      setError(
        err.response?.data?.message || "Failed to resend OTP. Please try again."
      );
    }
  };

  return (
    <div className="otp-verification">
      <h4>Enter verification code</h4>
      {byEmail ? (
        <div role="status" aria-live="polite">
          {showEmailNotice && <p className="otp-subtitle">Check your email for the code</p>}
        </div>
      ) : (
        <p className="otp-subtitle">
          We sent a 6-digit code to <strong>{phoneNumber}</strong>
        </p>
      )}

      {error && <Alert type="error" message="Error" description={error} />}

      <form onSubmit={handleSubmit}>
        <div className="otp-inputs">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              className="otp-digit"
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
            />
          ))}
        </div>

        <div className="action-row mt-4 mb-4">
          <SubmitButton label="Verify" isSubmitting={isSubmitting} />
        </div>

        <div className="otp-footer">
          <p className="otp-resend small-text">
            {resendTimer > 0 ? (
              <>Resend code in {formatWait(resendTimer)}</>
            ) : (
              <a onClick={handleResend}>
                Resend code
              </a>
            )}
          </p>

          {onBack && (
            <p className="otp-back small-text">
              <a onClick={onBack}>
                Change phone number
              </a>
            </p>
          )}
        </div>

        {/* Hidden badge + the attribution the reCAPTCHA terms require. The
            badge used to sit on top of "Change phone number" — see
            components/OtpCaptcha.jsx. */}
        <OtpCaptcha
          innerRef={recaptchaRef}
          enabled={captchaEnabled}
          siteKey={siteKey}
        />
      </form>
    </div>
  );
};

export default OTPVerification;
